import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Button, Select, Radio, Tag, Space, List, Typography, Empty, Spin, Divider,
  message as antMessage,
} from 'antd';
import {
  TeamOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import {
  getDashboardPermissions,
  grantDashboardPermission,
  updateDashboardPermission,
  revokeDashboardPermission,
  searchUsersForSharing,
  updateDashboard,
} from '../services/dashboardService';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';

const { Text, Title } = Typography;

/**
 * DashboardShareDialog — a modal for managing dashboard sharing permissions.
 * Replicates the UX from the Agentic Report app's Permission Management Dialog.
 */
export default function DashboardShareDialog({ open, onClose, dashboard, onUpdate }) {
  const { isAdmin } = useAuth();
  const { refreshGlobalDashboards } = useDashboard();
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [grantingPermission, setGrantingPermission] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPermissionType, setSelectedPermissionType] = useState('read');
  const [isPublic, setIsPublic] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);

  const loadPermissions = useCallback(async () => {
    if (!dashboard?.id) return;
    try {
      setLoadingPermissions(true);
      const perms = await getDashboardPermissions(dashboard.id);
      setPermissions(perms);
    } catch (err) {
      antMessage.error('Failed to load permissions');
    } finally {
      setLoadingPermissions(false);
    }
  }, [dashboard?.id]);

  const loadUsers = useCallback(async () => {
    try {
      const users = await searchUsersForSharing('');
      setAllUsers(users);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, []);

  useEffect(() => {
    if (open && dashboard) {
      setIsPublic(dashboard.is_public || false);
      setIsGlobal(dashboard.is_global || false);
      loadPermissions();
      loadUsers();
    }
  }, [open, dashboard, loadPermissions, loadUsers]);

  const handleGrantPermission = async () => {
    if (!dashboard || !selectedUserId || !selectedPermissionType) return;
    try {
      setGrantingPermission(true);
      await grantDashboardPermission(dashboard.id, selectedUserId, selectedPermissionType);
      antMessage.success('Permission granted successfully');
      setSelectedUserId(null);
      setSelectedPermissionType('read');
      await loadPermissions();
    } catch (err) {
      antMessage.error(err.message || 'Failed to grant permission');
    } finally {
      setGrantingPermission(false);
    }
  };

  const handleUpdatePermission = async (userId, newPermissionType) => {
    try {
      await updateDashboardPermission(dashboard.id, userId, newPermissionType);
      antMessage.success('Permission updated');
      await loadPermissions();
    } catch (err) {
      antMessage.error(err.message || 'Failed to update permission');
    }
  };

  const handleRevokePermission = async (userId) => {
    try {
      await revokeDashboardPermission(dashboard.id, userId);
      antMessage.success('Permission revoked');
      await loadPermissions();
    } catch (err) {
      antMessage.error(err.message || 'Failed to revoke permission');
    }
  };

  const handleTogglePublic = async () => {
    try {
      const newValue = !isPublic;
      await updateDashboard(dashboard.id, { is_public: newValue });
      setIsPublic(newValue);
      if (onUpdate) onUpdate({ ...dashboard, is_public: newValue });
      antMessage.success(newValue ? 'Dashboard is now public' : 'Dashboard is now private');
    } catch (err) {
      antMessage.error('Failed to update visibility');
    }
  };

  const handleToggleGlobal = async () => {
    try {
      const newValue = !isGlobal;
      await updateDashboard(dashboard.id, { is_global: newValue });
      setIsGlobal(newValue);
      if (onUpdate) onUpdate({ ...dashboard, is_global: newValue });
      refreshGlobalDashboards?.();
      antMessage.success(newValue ? 'Dashboard is now global (visible to all users)' : 'Dashboard is now non-global');
    } catch (err) {
      antMessage.error('Failed to update global visibility');
    }
  };

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined style={{ color: 'var(--theme-primary)' }} />
          <span>Share Dashboard — {dashboard?.name}</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Global Toggle (admin only) */}
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--theme-contentBg)', borderRadius: 8, border: '1px solid var(--theme-border)' }}>
            <Space>
              <GlobalOutlined style={{ fontSize: 16, color: isGlobal ? '#52c41a' : undefined }} />
              <div>
                <Text strong>Global Visibility</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Make this dashboard visible to all users in the system</Text>
              </div>
            </Space>
            <Button
              type={isGlobal ? 'primary' : 'default'}
              size="small"
              onClick={handleToggleGlobal}
              style={isGlobal ? { background: '#52c41a', borderColor: '#52c41a' } : {}}
            >
              {isGlobal ? 'Global' : 'Non-Global'}
            </Button>
          </div>
        )}

        {/* Public Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--theme-contentBg)', borderRadius: 8, border: '1px solid var(--theme-border)' }}>
          <Space>
            <GlobalOutlined style={{ fontSize: 16 }} />
            <div>
              <Text strong>Public Access</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>Anyone in the organization can view this dashboard</Text>
            </div>
          </Space>
          <Button
            type={isPublic ? 'primary' : 'default'}
            size="small"
            onClick={handleTogglePublic}
          >
            {isPublic ? 'Public' : 'Private'}
          </Button>
        </div>

        {/* Current Permissions */}
        <div>
          <Title level={5}>Current Access</Title>
          {loadingPermissions ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
          ) : permissions.length > 0 ? (
            <List
              dataSource={permissions}
              renderItem={(perm) => (
                <List.Item
                  actions={[
                    <Select
                      value={perm.permission_type}
                      onChange={(value) => handleUpdatePermission(perm.user_id, value)}
                      style={{ width: 130 }}
                      size="small"
                      disabled={grantingPermission}
                    >
                      <Select.Option value="edit">
                        <Space size={4}>
                          <EditOutlined />
                          <span>Edit</span>
                        </Space>
                      </Select.Option>
                      <Select.Option value="read">
                        <Space size={4}>
                          <EyeOutlined />
                          <span>Read-Only</span>
                        </Space>
                      </Select.Option>
                    </Select>,
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRevokePermission(perm.user_id)}
                      disabled={grantingPermission}
                    >
                      Revoke
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'var(--theme-primary)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18,
                        fontWeight: 'bold',
                      }}>
                        {(perm.username || perm.email).charAt(0).toUpperCase()}
                      </div>
                    }
                    title={
                      <Space direction="vertical" size={2}>
                        <Text strong>{perm.username}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <MailOutlined style={{ marginRight: 4 }} />
                          {perm.email}
                        </Text>
                      </Space>
                    }
                    description={
                      perm.full_name ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>{perm.full_name}</Text>
                      ) : null
                    }
                  />
                </List.Item>
              )}
              bordered
              style={{ marginTop: 8 }}
            />
          ) : (
            <Empty
              description="No additional permissions granted"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: '20px 0' }}
            />
          )}
        </div>

        <Divider />

        {/* Grant New Permission */}
        <div>
          <Title level={5}>Grant New Permission</Title>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Select User:</Text>
              <Select
                placeholder="Choose a user"
                style={{ width: '100%', marginTop: 8 }}
                showSearch
                filterOption={(input, option) => {
                  const s = input.toLowerCase();
                  return (
                    option?.username?.toLowerCase().includes(s) ||
                    option?.email?.toLowerCase().includes(s)
                  );
                }}
                optionRender={(option) => (
                  <Space direction="vertical" size={2}>
                    <Text strong>{option.data.username}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <MailOutlined style={{ marginRight: 4 }} />
                      {option.data.email}
                    </Text>
                  </Space>
                )}
                options={allUsers.map(user => ({
                  value: user.id,
                  label: `${user.username} (${user.email})`,
                  username: user.username,
                  email: user.email,
                }))}
                value={selectedUserId}
                onChange={setSelectedUserId}
              />
            </div>

            <div>
              <Text strong>Permission Type:</Text>
              <Radio.Group
                value={selectedPermissionType}
                onChange={e => setSelectedPermissionType(e.target.value)}
                style={{ marginTop: 8, width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio value="edit">
                    <Space>
                      <Tag color="blue" icon={<EditOutlined />}>Edit</Tag>
                      <Text type="secondary">Can view and edit the dashboard</Text>
                    </Space>
                  </Radio>
                  <Radio value="read">
                    <Space>
                      <Tag icon={<EyeOutlined />}>Read-Only</Tag>
                      <Text type="secondary">Can only view the dashboard</Text>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleGrantPermission}
              disabled={!selectedUserId || !selectedPermissionType || grantingPermission}
              loading={grantingPermission}
              block
            >
              Grant Permission
            </Button>
          </Space>
        </div>
      </Space>
    </Modal>
  );
}
